/**
 * Compliance: KYC-gate · cảnh báo thuế · cash-limit (US-E19.F4-0203-S03 · @UC9310) — @gate money 🔴.
 *
 * THUẦN: không DB, không I/O, KHÔNG `Date.now()`, KHÔNG `Math.random()` — cùng input ⇒ cùng output.
 *
 * 🔴 NGUYÊN TẮC PO (ranh giới pháp lý): hệ thống **CHỈ CẢNH BÁO + LƯU CHỨNG TỪ**, KHÔNG tự tính,
 * KHÔNG tự khấu trừ, KHÔNG tự nộp thuế. Vì vậy `estimateTaxWarning` trả **metadata ước tính** kèm
 * `autoDeducted: false` (literal — KHÔNG tồn tại nhánh nào set true; contract cũng ép literal false).
 * KHÔNG có hàm nào ở đây sinh bút toán — module này KHÔNG import ledger.
 *
 * NO-FLOAT: mọi phép tiền là BigInt. Thuế = `interestMinor * rateBasisPoints / 10000n` — chia BigInt
 * (floor), KHÔNG `Number()`, KHÔNG `Math.round`. Rate = **basis point INTEGER** (500 = 5%) chứ không
 * phải float `0.05` (0.05 không biểu diễn chính xác được nhị phân ⇒ cấm).
 *
 * CHIA CÓ DƯ: khi ước tính thuế cho NHIỀU người nhận lợi tức, phần lợi tức mỗi người phải Σ = đúng
 * tổng ⇒ REUSE `distributeWithRemainder` (rounding-policy, E14) — KHÔNG viết công thức chia thứ hai.
 */

import { distributeWithRemainder } from "./rounding-policy.js";

/* ───────────────────────────── Khoá config (system_settings) ───────────────────────────── */

/** Ngưỡng BẮT BUỘC KYC người thụ hưởng khi hoàn/chi (chuỗi BigInt minor). */
export const KYC_THRESHOLD_MINOR_KEY = "kyc_threshold_minor";
/** Ngưỡng CẢNH BÁO tiền mặt lớn (chuỗi BigInt minor) — cảnh báo, KHÔNG chặn. */
export const CASH_LIMIT_MINOR_KEY = "cash_limit_minor";
/** Thuế suất ước tính, đơn vị **basis point INTEGER** (500 = 5%) — KHÔNG float. */
export const TAX_RATE_BASIS_POINTS_KEY = "tax_rate_basis_points";

export const COMPLIANCE_CONFIG_KEYS = [
  KYC_THRESHOLD_MINOR_KEY,
  CASH_LIMIT_MINOR_KEY,
  TAX_RATE_BASIS_POINTS_KEY,
] as const;
export type ComplianceConfigKey = (typeof COMPLIANCE_CONFIG_KEYS)[number];

/**
 * DEFAULT hệ thống (khớp seed migration `20260713220000_e19_compliance_invoice_kyc_tax`).
 *
 * 🔴 `kyc_threshold_minor = "0"` và `cash_limit_minor = "0"` ⇒ **TẮT MẶC ĐỊNH** (0 = "không đặt
 * ngưỡng" ⇒ `needsKyc`/`overCashLimit` luôn false) ⇒ mọi quỹ hiện hữu hoàn/chi Y NGUYÊN như trước
 * task này (**0 REGRESS**, cùng nguyên tắc `cooling_threshold_minor` của E19.F2). Compliance là lựa
 * chọn CHỦ ĐỘNG của nhóm: bật bằng cách đặt ngưỡng > 0.
 */
export const COMPLIANCE_DEFAULTS: {
  readonly kyc_threshold_minor: string;
  readonly cash_limit_minor: string;
  readonly tax_rate_basis_points: number;
} = {
  kyc_threshold_minor: "0",
  cash_limit_minor: "0",
  tax_rate_basis_points: 0,
};

/** Ngưỡng GỢI Ý khi nhóm bật (dùng cho UI/doc — KHÔNG phải default hiệu lực). */
export const KYC_SUGGESTED_THRESHOLD_MINOR = "20000000";
export const CASH_LIMIT_SUGGESTED_MINOR = "20000000";

/** Mẫu số basis-point (10000 bp = 100%). Chia BigInt ⇒ floor, no-float. */
export const BASIS_POINT_SCALE = 10_000n;
/** Trần thuế suất hợp lệ = 100% (chống config rác 999999%). */
export const TAX_RATE_MAX_BASIS_POINTS = 10_000;

/** Config compliance đã resolve (fund > group > system). */
export interface ComplianceConfig {
  readonly kycThresholdMinor: bigint;
  readonly cashLimitMinor: bigint;
  readonly taxRateBasisPoints: bigint;
}

/* ───────────────────────────── Type-guard config (jsonb bẩn ⇒ rơi tầng kế) ───────────────────────────── */

/** True nếu `value` là CHUỖI BigInt minor ≥ 0 (tiền qua jsonb/wire là string — no-float, no-number). */
export function isComplianceThresholdMinor(value: unknown): value is string {
  if (typeof value !== "string" || value.trim() === "") return false;
  if (!/^\d+$/.test(value.trim())) return false;
  try {
    return BigInt(value.trim()) >= 0n;
  } catch {
    return false;
  }
}

/** Parse chuỗi BigInt minor → bigint; bẩn ⇒ null (caller rơi tầng kế / hard-default — fail-safe). */
export function parseComplianceThresholdMinor(value: unknown): bigint | null {
  return isComplianceThresholdMinor(value) ? BigInt(value.trim()) : null;
}

/** True nếu `value` là basis-point INTEGER ∈ [0, 10000] (KHÔNG float rate). */
export function isTaxRateBasisPoints(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= TAX_RATE_MAX_BASIS_POINTS
  );
}

/* ───────────────────────────── (1) KYC-gate ───────────────────────────── */

/**
 * 🔴 Hoàn/chi ≥ ngưỡng ⇒ **BẮT BUỘC** có định danh người thụ hưởng (payee_kyc) TRƯỚC khi post.
 * Biên BẰNG NHAU ⇒ true (inclusive — "từ ngưỡng trở lên").
 * Ngưỡng ≤ 0 ⇒ TẮT (0 = "không đặt ngưỡng") — không bắt KYC mọi khoản ⇒ 0 regress.
 */
export function needsKyc(amountMinor: bigint, thresholdMinor: bigint): boolean {
  if (typeof amountMinor !== "bigint" || typeof thresholdMinor !== "bigint") return false;
  if (thresholdMinor <= 0n) return false;
  return amountMinor >= thresholdMinor;
}

/* ───────────────────────────── (2) Cash-limit (CẢNH BÁO, KHÔNG CHẶN) ───────────────────────────── */

/**
 * Giao dịch tiền mặt ≥ ngưỡng ⇒ **cờ cảnh báo** "nên chứng minh nguồn". Biên `==` ⇒ true.
 * 🔴 CHỈ CẢNH BÁO — quyết định KHÔNG chặn nằm ở api (hàm này không biết gì về chặn).
 */
export function overCashLimit(amountMinor: bigint, limitMinor: bigint): boolean {
  if (typeof amountMinor !== "bigint" || typeof limitMinor !== "bigint") return false;
  if (limitMinor <= 0n) return false;
  return amountMinor >= limitMinor;
}

/* ───────────────────────────── (3) Cảnh báo thuế (ƯỚC TÍNH, KHÔNG KHẤU TRỪ) ───────────────────────────── */

/** Loại cảnh báo thuế. */
export const TAX_WARNING_KIND = {
  /** Lợi tức chia cho thành viên — TNCN từ đầu tư vốn (người nhận tự kê khai). */
  PERSONAL_INCOME_INTEREST: "personal_income_interest",
  /** Không cần cảnh báo (rate = 0 hoặc lợi tức = 0). */
  NONE: "none",
} as const;
export type TaxWarningKind = (typeof TAX_WARNING_KIND)[keyof typeof TAX_WARNING_KIND];

export interface TaxWarningInput {
  readonly interestMinor: bigint;
  /** Thuế suất × basis point (500 = 5%) — INTEGER, KHÔNG float. */
  readonly rateBasisPoints: bigint;
}

export interface TaxWarningEstimate {
  readonly interestMinor: bigint;
  readonly taxEstimateMinor: bigint;
  readonly warningKind: TaxWarningKind;
  /** 🔴 BẤT BIẾN literal `false` — hệ thống KHÔNG BAO GIỜ tự khấu trừ/nộp thuế. */
  readonly autoDeducted: false;
}

/**
 * 🔴 ƯỚC TÍNH phần thuế trên lợi tức — **METADATA CẢNH BÁO**, KHÔNG khấu trừ, KHÔNG sinh bút toán.
 *
 * `taxEstimateMinor = floor(interestMinor * rateBasisPoints / 10000)` bằng **BigInt** (chia BigInt =
 * floor với số không âm ⇒ deterministic, không NaN, không sai số nhị phân).
 *
 * Input âm / rate ≤ 0 / interest = 0 ⇒ ước tính 0 + `warningKind='none'` (KHÔNG throw: đây là lớp
 * cảnh báo, không được làm hỏng luồng tiền hợp lệ).
 *
 * `autoDeducted` LUÔN `false` — không có tham số nào bật được nó.
 */
export function estimateTaxWarning(input: TaxWarningInput): TaxWarningEstimate {
  const interest =
    typeof input.interestMinor === "bigint" && input.interestMinor > 0n ? input.interestMinor : 0n;
  const rate =
    typeof input.rateBasisPoints === "bigint" && input.rateBasisPoints > 0n
      ? input.rateBasisPoints
      : 0n;

  if (interest === 0n || rate === 0n) {
    return {
      interestMinor: interest,
      taxEstimateMinor: 0n,
      warningKind: TAX_WARNING_KIND.NONE,
      autoDeducted: false,
    };
  }

  // BigInt division = floor (số không âm) — KHÔNG float, KHÔNG Math.round.
  const taxEstimateMinor = (interest * rate) / BASIS_POINT_SCALE;
  return {
    interestMinor: interest,
    taxEstimateMinor,
    warningKind: TAX_WARNING_KIND.PERSONAL_INCOME_INTEREST,
    autoDeducted: false,
  };
}

/* ───────────────────── (4) Chia lợi tức cho N người + ước tính thuế mỗi người ───────────────────── */

export interface InterestRecipient {
  readonly memberId: string;
  /** Trọng số (vd: phần vốn góp minor) — INTEGER > 0. */
  readonly weight: bigint;
}

export interface InterestShareWarning {
  readonly memberId: string;
  readonly interestMinor: bigint;
  readonly taxEstimateMinor: bigint;
}

export interface InterestTaxWarningResult {
  readonly totalInterestMinor: bigint;
  readonly totalTaxEstimateMinor: bigint;
  readonly warningKind: TaxWarningKind;
  readonly autoDeducted: false;
  readonly shares: readonly InterestShareWarning[];
}

/**
 * 🔴 Chia lợi tức cho N người theo trọng số rồi ước tính thuế TỪNG phần — CHỈ CẢNH BÁO.
 *
 * Σ `shares[].interestMinor` = `totalInterestMinor` **CHÍNH XÁC** (P1.3 — đồng lẻ có tên): REUSE
 * `distributeWithRemainder` (E14), dư dồn về recipient ĐẦU TIÊN (deterministic — không random).
 * `totalTaxEstimateMinor` = Σ thuế TỪNG phần (KHÔNG tính lại trên tổng ⇒ tổng luôn khớp phần).
 *
 * KHÔNG khấu trừ: shares giữ NGUYÊN interestMinor (thuế chỉ là con số kèm theo để người nhận tự kê
 * khai). `autoDeducted: false`.
 */
export function estimateInterestTaxWarning(input: {
  readonly totalInterestMinor: bigint;
  readonly rateBasisPoints: bigint;
  readonly recipients: readonly InterestRecipient[];
}): InterestTaxWarningResult {
  const total = input.totalInterestMinor > 0n ? input.totalInterestMinor : 0n;
  const summary = estimateTaxWarning({
    interestMinor: total,
    rateBasisPoints: input.rateBasisPoints,
  });

  if (input.recipients.length === 0) {
    return {
      totalInterestMinor: total,
      totalTaxEstimateMinor: 0n,
      warningKind: summary.warningKind,
      autoDeducted: false,
      shares: [],
    };
  }

  // REUSE rounding-policy (KHÔNG viết công thức chia thứ hai) — Σ = total chặt, dư dồn index 0.
  const parts = distributeWithRemainder(
    total,
    input.recipients.map((r) => r.weight),
    0,
  );

  let totalTax = 0n;
  const shares: InterestShareWarning[] = [];
  for (let i = 0; i < input.recipients.length; i++) {
    const recipient = input.recipients[i] as InterestRecipient;
    const interestMinor = parts[i] as bigint;
    const est = estimateTaxWarning({
      interestMinor,
      rateBasisPoints: input.rateBasisPoints,
    });
    totalTax += est.taxEstimateMinor;
    shares.push({
      memberId: recipient.memberId,
      interestMinor,
      taxEstimateMinor: est.taxEstimateMinor,
    });
  }

  return {
    totalInterestMinor: total,
    // Σ thuế TỪNG phần (≤ thuế trên tổng do floor mỗi phần) — nguồn sự thật là phần, không phải tổng.
    totalTaxEstimateMinor: totalTax,
    warningKind: summary.warningKind,
    autoDeducted: false,
    shares,
  };
}

/* ───────────────────────────── (5) Chứng từ = CLAIM (evidence-là-claim, P7.3) ───────────────────────────── */

export const DOC_TYPE = {
  VAT_INVOICE: "vat_invoice",
  RECEIPT: "receipt",
  CONTRACT: "contract",
  OTHER: "other",
} as const;
export type DocType = (typeof DOC_TYPE)[keyof typeof DOC_TYPE];
export const DOC_TYPES = Object.values(DOC_TYPE) as readonly DocType[];

export const VERIFICATION_STATUS = {
  VALID: "valid",
  INVALID: "invalid",
  UNVERIFIED: "unverified",
} as const;
export type VerificationStatus =
  (typeof VERIFICATION_STATUS)[keyof typeof VERIFICATION_STATUS];
export const VERIFICATION_STATUSES = Object.values(
  VERIFICATION_STATUS,
) as readonly VerificationStatus[];

/**
 * 🔴 EVIDENCE-LÀ-CLAIM (P7.3): chứng từ MỚI đính kèm LUÔN ở `unverified` — hệ thống KHÔNG tin mù.
 * Chuyển sang `valid`/`invalid` là hành động CÓ CHỦ THỂ (admin/đồng thuận — F5), không tự động.
 * Hằng số này là nguồn sự thật duy nhất cho api + DB DEFAULT (migration).
 */
export const DEFAULT_VERIFICATION_STATUS: VerificationStatus = VERIFICATION_STATUS.UNVERIFIED;
